// The Dashboard's left sidebar (Brief 4 / P8) — the spine of the left→right
// descent. The Global room is pinned on top (general cross-project chat); below
// it, the list of projects you've created, then the Unassigned bucket when any
// session has no project. A project carries a needs-me badge (the one Notice
// signal allowed in the chrome — STATIC, never moving) and a calm total count.
// "+ New project" creates one inline. The footer holds the appearance control
// and the way to the other standing surfaces (Govern, Radio). Token-pure.

import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { usePresenceContext } from "@waytide/ui";
import {
  UNASSIGNED_PROJECT_ID,
  projectNeedsCounts,
  projectSessionCounts,
} from "@waytide/ui";
import { useProjectsContext } from "../projects/ProjectsContext";
import { projectNameError } from "../projects/projectModel";
import type { ProjectSummary } from "@waytide/core";
import { Wordmark } from "../../receipt/ReceiptKit";

function navClass({ isActive }: { isActive: boolean }): string {
  return "wt-dash-nav" + (isActive ? " wt-dash-nav--active" : "");
}

/** A project row in the sidebar — a NavLink whose name double-clicks into an
 * inline rename (Enter / blur saves, Esc cancels). Rename is an owner action,
 * gated server-side; a non-owner's PATCH 403s and the error surfaces inline. */
function ProjectRow({
  p,
  needs,
  total,
}: {
  p: ProjectSummary;
  needs: number;
  total: number;
}) {
  const { rename } = useProjectsContext();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.name);
  const [err, setErr] = useState("");

  function begin() {
    setDraft(p.name);
    setErr("");
    setEditing(true);
  }

  async function commit() {
    const next = draft.trim();
    if (next === "" || next === p.name) {
      setEditing(false);
      return;
    }
    const problem = projectNameError(next);
    if (problem) {
      setErr(problem);
      return;
    }
    try {
      await rename(p.project_id, next);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  if (editing) {
    return (
      <li>
        <input
          className="wt-dash-create__input wt-dash-rename"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") setEditing(false);
          }}
          aria-label={`Rename ${p.name}`}
        />
        {err ? <p className="wt-dash-create__err">{err}</p> : null}
      </li>
    );
  }

  return (
    <li>
      <NavLink to={`/dashboard/p/${p.project_id}`} className={navClass}>
        <span
          className="wt-dash-nav__label"
          onDoubleClick={(e) => {
            e.preventDefault();
            begin();
          }}
          title="Double-click to rename"
        >
          {p.name}
        </span>
        <CountBadge needs={needs} total={total} />
      </NavLink>
    </li>
  );
}

/** A project row's count: the needs-me badge (amber, static) when any session
 * in it needs you, else the calm total. */
function CountBadge({ needs, total }: { needs: number; total: number }) {
  if (needs > 0) {
    return (
      <span className="wt-dash-badge" data-testid="needs-badge">
        {needs}
      </span>
    );
  }
  if (total > 0) {
    return <span className="wt-dash-count">{total}</span>;
  }
  return null;
}

export function Sidebar() {
  const { sessions, needsCount } = usePresenceContext();
  const { projects, create } = useProjectsContext();
  const counts = projectSessionCounts(sessions);
  const needs = projectNeedsCounts(sessions);
  const unassignedCount = counts.get(UNASSIGNED_PROJECT_ID) ?? 0;

  const [draft, setDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const problem = projectNameError(draft);
    if (problem) {
      setErr(problem);
      return;
    }
    setErr("");
    setCreating(true);
    try {
      await create(draft.trim());
      setDraft("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  return (
    <nav className="wt-dash-sidebar" aria-label="Dashboard">
      <div className="wt-dash-sidebar__brand">
        <Link to="/" className="wt-dash-sidebar__home" aria-label="Waytide — home">
          <Wordmark />
        </Link>
      </div>

      {/* Global room — pinned to the top. */}
      <NavLink to="/dashboard/global" className={navClass}>
        <span className="wt-dash-nav__label"># Global</span>
        {needsCount > 0 ? (
          <span className="wt-dash-badge">{needsCount}</span>
        ) : null}
      </NavLink>

      <div className="wt-dash-sidebar__heading">Projects</div>
      <ul className="wt-dash-projects">
        {projects.map((p) => (
          <ProjectRow
            key={p.project_id}
            p={p}
            needs={needs.get(p.project_id) ?? 0}
            total={counts.get(p.project_id) ?? 0}
          />
        ))}
        {unassignedCount > 0 ? (
          <li>
            <NavLink
              to={`/dashboard/p/${UNASSIGNED_PROJECT_ID}`}
              className={navClass}
            >
              <span className="wt-dash-nav__label wt-dash-nav__label--muted">
                Unassigned
              </span>
              <CountBadge
                needs={needs.get(UNASSIGNED_PROJECT_ID) ?? 0}
                total={unassignedCount}
              />
            </NavLink>
          </li>
        ) : null}
      </ul>

      <form className="wt-dash-create" onSubmit={submit}>
        <input
          className="wt-dash-create__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="New project…"
          aria-label="New project name"
        />
        <button
          type="submit"
          className="wt-dash-create__btn"
          disabled={creating || !draft.trim()}
          aria-label="Create project"
        >
          {creating ? "…" : "+"}
        </button>
      </form>
      {err ? <p className="wt-dash-create__err">{err}</p> : null}

      <div className="wt-dash-sidebar__spacer" />
      <div className="wt-dash-sidebar__footer">
        <NavLink to="/dashboard/pull-requests" className={navClass}>
          <span className="wt-dash-nav__label">Pull requests</span>
        </NavLink>
        <NavLink to="/dashboard/adherence" className={navClass}>
          <span className="wt-dash-nav__label">Adherence</span>
        </NavLink>
        <NavLink to="/dashboard/corpus" className={navClass}>
          <span className="wt-dash-nav__label">Corpus</span>
        </NavLink>
        <NavLink to="/govern" className={navClass}>
          <span className="wt-dash-nav__label">Govern</span>
        </NavLink>
        <NavLink to="/radio" className={navClass}>
          <span className="wt-dash-nav__label">Radio</span>
        </NavLink>
      </div>
    </nav>
  );
}
